import { createContext, useContext, type ReactNode } from "react";
import { cx } from "../utils/format";
import { Icon } from "../icons/Icon";

/** Who sees a settings place (the founder's rule of 2026-10-05):
 *  - `platform`: a placeholder for something not built yet; only the application's own administrators see it.
 *  - `admins`: only workspace administrators see it (the screen hides it from the rest; the tag tells the founder so).
 *  - `locked`: members see it but cannot change it.
 *  The tag itself is for administrators only (2026-10-05): see `useTagShown`. */
export type Audience = "platform" | "admins" | "locked";
/** the person looking at the page, as the app knows them */
export type AudienceViewer = { platformAdmin: boolean; workspaceAdmin: boolean };

/** the tags' words; the locked one (the founder's wording, 2026-10-22) is the icon tag's tooltip and accessible name */
export const AUDIENCE_TEXT: Record<Audience, string> = { platform: "Coming · admin only", admins: "Admins", locked: "Team members see this locked" };

const NOBODY: AudienceViewer = { platformAdmin: false, workspaceAdmin: false };
const Ctx = createContext<AudienceViewer>(NOBODY);

/** The app says who is looking; without it nobody is a platform administrator, so `platform` places stay hidden. */
export function AudienceProvider({ value, children }: { value: AudienceViewer; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
export const useAudience = (): AudienceViewer => useContext(Ctx);

/** false only for a `platform` place and a viewer who is not a platform administrator */
export function useShows(audience: Audience | undefined): boolean {
  const viewer = useAudience();
  return audience !== "platform" || viewer.platformAdmin;
}

/** Whether the viewer gets a place's tag (2026-10-05, DECISIONS "Members in Settings"): the tags tell administrators who else sees the
 *  place, so `admins` and `locked` tag only for a workspace administrator; a `platform` place shows only to a platform administrator,
 *  tag included. A member sees a locked place without the note. */
export function useTagShown(audience: Audience | undefined): boolean {
  const viewer = useAudience();
  return !!audience && (audience === "platform" ? viewer.platformAdmin : viewer.workspaceAdmin);
}

/** The small tag that says who sees a place, for the viewers `useTagShown` allows. A note for screen readers. The locked tag (the first
 *  setup round, 2026-10-22) is the eye-with-a-lock icon in the muted colour with the kit's tooltip, a keyboard stop with the same words as
 *  its accessible name; the other two stay words. */
export function AudienceTag({ audience }: { audience: Audience }) {
  if (!useTagShown(audience)) return null;
  if (audience === "locked") return <span className="ob-audience ob-audience--locked ob-audience--icon" role="note" aria-label={AUDIENCE_TEXT.locked} data-tip={AUDIENCE_TEXT.locked} tabIndex={0}><Icon name="visibilityLock" size="sm" /></span>;
  return <span className={cx("ob-audience", `ob-audience--${audience}`)} role="note">{AUDIENCE_TEXT[audience]}</span>;
}

/** A place that is not a form section or row (an integration's row, a menu entry, a tab's page): it renders only for the viewers the
 *  audience allows, and `tag` adds the tag before its content. */
export function ForAudience({ audience, tag, children }: { audience: Audience; tag?: boolean; children?: ReactNode }) {
  if (!useShows(audience)) return null;
  return <>{tag && <AudienceTag audience={audience} />}{children}</>;
}
